import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--color-gray-200);
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const StoryTitle = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const CommentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CommentLabel = styled.label`
  font-weight: 600;
  color: var(--color-gray-700);
  font-size: 1rem;
`;

const CommentTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.75rem;
  border: 2px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.9rem;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-light);
  }
  
  &::placeholder {
    color: var(--color-gray-500);
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.8rem;
  color: var(--color-gray-500);
`;

const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem 2rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  border-top: 1px solid var(--color-gray-200);
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${({ variant = 'secondary' }) =>
    variant === 'primary'
      ? `
          background: var(--color-primary);
          color: var(--color-white);
          border-color: var(--color-primary);
          
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `
      : `
          background: var(--color-white);
          color: var(--color-gray-700);
          border-color: var(--color-gray-300);
          
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
            border-color: var(--color-gray-400);
          }
        `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: var(--color-red-50);
  color: var(--color-red-700);
  padding: 0.75rem;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-red-200);
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

interface CommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
  storyTitle: string;
  isSubmitting?: boolean;
}

const CommentModal: React.FC<CommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  storyTitle,
  isSubmitting = false
}) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  
  const maxCommentLength = 1000;
  
  useEffect(() => {
    if (isOpen) {
      setComment('');
      setError('');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleSubmit = async () => {
    const trimmedComment = comment.trim();
    
    if (trimmedComment === '') {
      setError('Please add a comment before submitting.');
      return;
    }
    
    if (comment.length > maxCommentLength) {
      setError(`Comment must be ${maxCommentLength} characters or less.`);
      return;
    }
    
    try {
      setError('');
      await onSubmit(trimmedComment);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit comment. Please try again.');
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add Comment</ModalTitle>
          <StoryTitle>"{storyTitle}"</StoryTitle>
        </ModalHeader>
        
        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <CommentSection>
            <CommentLabel htmlFor="comment">
              Your Comment
            </CommentLabel>
            <CommentTextarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts about this story..."
              maxLength={maxCommentLength}
              disabled={isSubmitting}
              autoFocus
            />
            <CharacterCount>
              {comment.length} / {maxCommentLength} characters
            </CharacterCount>
          </CommentSection>
        </ModalBody>
        
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || comment.trim() === ''}
          >
            {isSubmitting ? 'Submitting...' : 'Add Comment'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CommentModal;